/**
 * Sprint 5-a · StepProgress · 逐步製作（串流逐字 render + 中斷 button）。
 * 串流為 server-side 分塊模擬（adapter 為 sync·非 Anthropic 原生 token 串流）。
 */
import React from 'react';
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import StopIcon from '@mui/icons-material/Stop';
import { useTranslation } from 'react-i18next';

interface Props {
  stepNo: number;          // 1-based 顯示
  totalSteps: number;
  title: string;
  streamingText: string;
  running: boolean;        // true = 串流中（顯示 spinner + 中斷）
  doneTokens?: number;     // step_done 後顯示
  doneCost?: number;
  cumulativeTokens?: number;
  onAbort: () => void;
}

const StepProgress: React.FC<Props> = ({
  stepNo, totalSteps, title, streamingText, running, doneTokens, doneCost, cumulativeTokens, onAbort,
}) => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`agentic.${k}`, d);
  return (
    <Paper variant="outlined" sx={{ p: 1.25, mb: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Chip size="small" color="primary" label={`${tt('step', '步驟')} ${stepNo}/${totalSteps}`} />
        <Typography variant="body2" sx={{ flex: 1 }}><strong>{title}</strong></Typography>
        {running && (
          <Button size="small" color="warning" variant="outlined" startIcon={<StopIcon />} onClick={onAbort}>
            {tt('abort', '中斷')}
          </Button>
        )}
      </Stack>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', minHeight: 20 }}>
        {streamingText}
        {running && <CircularProgress size={10} sx={{ ml: 0.5 }} />}
      </Typography>
      {doneTokens != null && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          ✓ {doneTokens} tokens · ${(doneCost || 0).toFixed(4)}
          {cumulativeTokens != null && <> · {tt('cumulative', '累計')} {cumulativeTokens.toLocaleString()}</>}
        </Typography>
      )}
    </Paper>
  );
};

export default StepProgress;
